import type { KeywordDefinition } from 'ajv'
import { AddressValidationAPI } from './AddressValidationAPI'

const STREET_FIELDS = ['streetAddress', 'street1', 'address1']

/**
 * Async AJV keyword that warns when an address is a P.O. Box.
 *
 * Set `"allowPOBox": false` on an address object in a warningSchema to enable.
 * The parent schema must declare `"$async": true` for async validation to run.
 *
 * Example warningSchema:
 * ```json
 * {
 *   "$async": true,
 *   "type": "object",
 *   "properties": {
 *     "mailingAddress": {
 *       "type": "object",
 *       "allowPOBox": false
 *     }
 *   }
 * }
 * ```
 */
export const allowPOBox: KeywordDefinition = {
  keyword: 'allowPOBox',
  type: 'object',
  schemaType: 'boolean',
  async: true,
  errors: true,
  validate: async function validateAllowPOBox(
    schema: boolean,
    data: Record<string, string>
  ): Promise<boolean> {
    if (schema !== false) return true

    const streetValue = STREET_FIELDS.reduce<string | undefined>(
      (found, key) => found ?? (data[key] || undefined),
      undefined
    )

    if (!streetValue) return true

    try {
      const result = await AddressValidationAPI.validate({
        street: streetValue,
        city: data.city,
        state: data.state,
        zipCode: data.postalCode ?? data.zipCode ?? data.zip,
      })

      if (result.isPOBox) {
        ;(validateAllowPOBox as any).errors = [
          {
            keyword: 'allowPOBox',
            message: 'P.O. Boxes are not accepted for this field',
            params: { allowPOBox: false },
          },
        ]
        return false
      }

      return true
    } catch {
      // Don't block the user on network/service errors
      return true
    }
  },
}
